import React, {useState, useEffect} from "react";
import {useNavigate} from "react-router-dom";
import {
    redirectToLoginIfInvalidToken,
    fetchAllReservationCount,
    loadAllReservations,
    cancelAdminReservation,
    updateReservationByAdmin
} from "../../api/api";
import styles from './../MyReservations/MyReservations.module.css';
import {getStatus, formatDate, formatTime, canAdminCancelReservation} from "../../utils";
import ReservationCancelModal from "../../UI/Modal/ReservationCancelModal/ReservationCancelModal";
import CustomModal from "../../UI/Modal/CustomModal";
import Spinner from "../Spinner/Spinner";


const AllUsersReservations = () => {
    const [reservations, setReservations] = useState([]);
    const [reservationType, setReservationType] = useState("current");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageCount, setPageCount] = useState(0);
    const [hasNextPage, setHasNextPage] = useState(true);
    const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
    const [cancelingReservationId, setCancelingReservationId] = useState(null);
    const [isReservationCancelModalOpen, setIsReservationCancelModalOpen] = useState(false);
    const [reservationCancelMessage, setReservationCancelMessage] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [editingReservation, setEditingReservation] = useState(null);
    const [editError, setEditError] = useState('');
    const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
    const [infoMessage, setInfoMessage] = useState('');

    const navigate = useNavigate();

    useEffect(() => {
        redirectToLoginIfInvalidToken(navigate);
    }, [navigate]);

    useEffect(() => {
        const checkToken = () => {
            redirectToLoginIfInvalidToken(navigate);
        };

        window.addEventListener("storage", checkToken);
        return () => window.removeEventListener("storage", checkToken);
    }, [navigate]);

    const fetchAllReservations = async () => {
        try {
            const fetchedAllReservations = await loadAllReservations(currentPage - 1, reservationType);

            const sortedReservations = fetchedAllReservations.sort((a, b) => {
                if (reservationType === "current") {
                    return new Date(a.startTime) - new Date(b.startTime);
                } else {
                    return new Date(b.startTime) - new Date(a.startTime);
                }
            });
            setReservations(sortedReservations);
        } catch (error) {
            if (error.message === "Invalid or missing token") {
                navigate("/login", {replace: true});
            } else {
                console.error("Error while fetching reservations:", error);
            }
        }finally {
            setIsLoading(false);
        }
    };

    const fetchPageCount = async () => {
        try {
            const count = await fetchAllReservationCount(reservationType);
            const countAsNumber = Number(count);
            setPageCount(Math.ceil(countAsNumber / 10));
            setHasNextPage(countAsNumber > currentPage * 10);
        } catch (error) {
            if (error.message === "Invalid or missing token") {
                navigate("/login", {replace: true});
            } else {
                console.error("Error while fetching page count:", error);
            }
        }
    };


    useEffect(() => {
        fetchAllReservations();
    }, [reservationType, currentPage]);


    useEffect(() => {
        fetchPageCount();
    }, [reservationType, currentPage]);


    useEffect(() => {
        setCurrentPage(1);
        setPageCount(0);
    }, [reservationType]);

    const handleButtonCancelClick = (reservationId) => {
        setCancelingReservationId(reservationId);
        setIsCancelModalOpen(true);
    };


    const handleConfirmCancel = async (sendEmail, emailMessage) => {
        if (cancelingReservationId) {
            try {
                await cancelAdminReservation(cancelingReservationId, sendEmail, emailMessage);
                fetchAllReservations();

                setReservationCancelMessage('Rezerwacja anulowana, e-mail wysłany.');
            } catch (error) {
                console.error('Error canceling reservation:', error);
                console.error('Error details:', error.response);

                if (error.message === "Invalid or missing token") {
                    navigate("/login", {replace: true});
                }

                setReservationCancelMessage('Wystąpił błąd podczas anulowania rezerwacji.');
                setIsReservationCancelModalOpen(true);
            } finally {
                setCancelingReservationId(null);
                setIsCancelModalOpen(false);
            }
        }
        setIsCancelModalOpen(false);
        setIsReservationCancelModalOpen(true);
    };


    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    const toLocalDateTimeInput = (iso) => {
        if (!iso) return '';
        const d = new Date(iso);
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };

    const handleOpenEdit = (reservation) => {
        setEditingReservation({
            id: reservation.id,
            status: reservation.status,
            cost: reservation.cost ?? 0,
            startTime: toLocalDateTimeInput(reservation.startTime),
            endTime: toLocalDateTimeInput(reservation.endTime),
        });
        setEditError('');
    };

    const handleEditFieldChange = (field, value) => {
        setEditingReservation(prev => ({ ...prev, [field]: value }));
    };

    const handleSaveEdit = async () => {
        if (!editingReservation) return;
        if (editingReservation.startTime && editingReservation.endTime &&
            new Date(editingReservation.endTime) <= new Date(editingReservation.startTime)) {
            setEditError('Czas zakończenia musi być po czasie rozpoczęcia.');
            return;
        }
        try {
            await updateReservationByAdmin({
                id: editingReservation.id,
                status: editingReservation.status,
                cost: editingReservation.cost === '' ? null : parseFloat(editingReservation.cost),
                startTime: editingReservation.startTime ? `${editingReservation.startTime}:00` : null,
                endTime: editingReservation.endTime ? `${editingReservation.endTime}:00` : null,
            });
            setEditingReservation(null);
            setInfoMessage(`Rezerwacja nr ${editingReservation.id} zaktualizowana.`);
            setIsInfoModalOpen(true);
            fetchAllReservations();
        } catch (error) {
            const msg = error?.response?.data || 'Wystąpił błąd podczas zapisu.';
            setEditError(typeof msg === 'string' ? msg : JSON.stringify(msg));
        }
    };

    if (isLoading) return <Spinner size={"large"}/>;

    return (
        <div className={styles.MyReservations}>
            <div className={styles.radioButtons}>
                <label>
                    <input
                        type="radio"
                        value="current"
                        checked={reservationType === "current"}
                        onChange={(e) => setReservationType(e.target.value)}
                    />
                    Aktualne
                </label>
                <label>
                    <input
                        type="radio"
                        value="past"
                        checked={reservationType === "past"}
                        onChange={(e) => setReservationType(e.target.value)}
                    />
                    Przeszłe
                </label>
            </div>

            <ReservationCancelModal
                isOpen={isCancelModalOpen}
                onRequestClose={() => setIsCancelModalOpen(false)}
                message="Czy na pewno chcesz anulować tę rezerwację?"
                isPositiveMessage={false}
                confirmLabel="Anuluj rezerwację"
                onConfirm={handleConfirmCancel}
                confirmButtonColor="red"
                cancelLabel="Nie anuluj"
                onCancel={() => setIsCancelModalOpen(false)}
                cancelButtonColor="green"
            />
            <ReservationCancelModal
                isOpen={isReservationCancelModalOpen}
                onRequestClose={() => setIsReservationCancelModalOpen(false)}
                message={reservationCancelMessage}
                isPositiveMessage={true}
                confirmLabel="OK"
                onConfirm={() => setIsReservationCancelModalOpen(false)}
                showEmailForm={false} // Ukryj formularz wiadomości e-mail

            />

            <div className={styles.tableContainer}>

                {reservations.length === 0 ? (
                    <div className={styles.noReservations}>
                        Brak rezerwacji dla wybranego okresu
                    </div>
                ) : (
                    <table className={styles.reservationsTable}>
                        <thead>
                        <tr>
                            <th>Nr</th>
                            <th>Data</th>
                            <th>Nazwa</th>
                            <th>Kort</th>
                            <th>Czas</th>
                            <th>Stat</th>
                            <th></th>
                            <th>Kwota</th>
                        </tr>
                        </thead>
                        <tbody>
                        {reservations.map(({
                                               id,
                                               status,
                                               startTime,
                                               endTime,
                                               firstName,
                                               lastName,
                                               courtId,
                                               cost,
                                           }) => (

                            <tr key={id}
                                className={
                                    status === "CANCELED"
                                        ? styles.cancelledReservation
                                        : status === "PAYED"
                                            ? styles.payedReservation
                                            : status === "REGISTERED" && new Date(startTime) < new Date()
                                                ? styles.expiredReservation
                                                : ""
                                }
                            >

                                <td>{id}</td>
                                <td>{formatDate(startTime, reservationType === "past")}</td>
                                <td>{`${firstName} ${lastName}`}</td>
                                <td>{courtId}</td>
                                <td>{formatTime(startTime)} - {formatTime(endTime)}</td>
                                <td>{getStatus(status)}</td>
                                <td>
                                    <div className={styles.cancelButtonContainer} style={{ display: 'flex', gap: '4px' }}>
                                        {canAdminCancelReservation(status) && (
                                            <button className={styles.button}
                                                    onClick={() => handleButtonCancelClick(id)}>
                                                X
                                            </button>
                                        )}
                                        <button
                                            className={styles.button}
                                            onClick={() => handleOpenEdit({ id, status, startTime, endTime, cost })}
                                            title="Edytuj rezerwację"
                                        >
                                            ✎
                                        </button>
                                    </div>
                                </td>
                                <td>
                                    {(status === "REGISTERED" || status === "PAYED" || status === "LESSONS" || status === "NMEMBER") ? cost.toFixed(2) : ""}
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>

                )}
            </div>
            {editingReservation && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.45)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', zIndex: 1000
                }}>
                    <div style={{
                        background: '#fff', padding: '20px', borderRadius: '8px',
                        minWidth: '320px', maxWidth: '90vw', boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
                    }}>
                        <h3 style={{ marginTop: 0 }}>Edycja rezerwacji nr {editingReservation.id}</h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            <label>
                                Czas rozpoczęcia:
                                <input
                                    type="datetime-local"
                                    value={editingReservation.startTime}
                                    onChange={e => handleEditFieldChange('startTime', e.target.value)}
                                    style={{ width: '100%', padding: '6px', marginTop: '4px' }}
                                />
                            </label>
                            <label>
                                Czas zakończenia:
                                <input
                                    type="datetime-local"
                                    value={editingReservation.endTime}
                                    onChange={e => handleEditFieldChange('endTime', e.target.value)}
                                    style={{ width: '100%', padding: '6px', marginTop: '4px' }}
                                />
                            </label>
                            <label>
                                Status:
                                <select
                                    value={editingReservation.status}
                                    onChange={e => handleEditFieldChange('status', e.target.value)}
                                    style={{ width: '100%', padding: '6px', marginTop: '4px' }}
                                >
                                    <option value="REGISTERED">REGISTERED</option>
                                    <option value="PAYED">PAYED</option>
                                    <option value="MEMBER">MEMBER</option>
                                    <option value="NMEMBER">NMEMBER</option>
                                    <option value="LESSONS">LESSONS</option>
                                    <option value="PERIODIC">PERIODIC</option>
                                    <option value="LEAGUE">LEAGUE</option>
                                    <option value="BLOCKED">BLOCKED</option>
                                    <option value="CANCELED">CANCELED</option>
                                </select>
                            </label>
                            <label>
                                Kwota (zł):
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={editingReservation.cost}
                                    onChange={e => handleEditFieldChange('cost', e.target.value)}
                                    style={{ width: '100%', padding: '6px', marginTop: '4px' }}
                                />
                            </label>
                        </div>
                        {editError && (
                            <div style={{ color: '#c62828', marginTop: '10px', fontSize: '0.9rem' }}>
                                {editError}
                            </div>
                        )}
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
                            <button onClick={() => setEditingReservation(null)} style={{ padding: '6px 14px' }}>
                                Anuluj
                            </button>
                            <button onClick={handleSaveEdit} style={{
                                padding: '6px 14px', background: '#2e7d32', color: '#fff', border: 'none', borderRadius: '4px'
                            }}>
                                Zapisz
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <CustomModal
                isOpen={isInfoModalOpen}
                onRequestClose={() => setIsInfoModalOpen(false)}
                message={infoMessage}
                isPositiveMessage={true}
                cancelLabel="OK"
            />

            <div className={styles.pagination}>
                {pageCount > 1 && (
                    <>
                        <button
                            className={styles.prevNextButton}
                            disabled={currentPage === 1}
                            onClick={() => handlePageChange(currentPage - 1)}
                        >
                            Poprzednia
                        </button>
                        <span className={styles.pageNumber}>{currentPage}</span>
                        <button
                            className={styles.prevNextButton}
                            disabled={!hasNextPage}
                            onClick={() => handlePageChange(currentPage + 1)}
                        >
                            Następna
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default AllUsersReservations;
